import React, { useCallback, useEffect, useState } from 'react';
import { Store, Plus, RefreshCw, KeyRound, Wallet, Lock, Power, Copy } from 'lucide-react';
import api, { asList } from '../services/api';

const NAVY = '#1B3A5C';
const GREEN = '#43A047';
const BG = '#F5F7FA';
const FONT = "'Poppins', sans-serif";
const DASHBOARD_URL = 'https://merchant.moneyplus-pay.com';

interface Merchant {
  id: number;
  name: string;
  email: string;
  phone?: string;
  api_key_prefix: string;
  balance: number;
  currency: string;
  is_active: boolean;
  has_password: boolean;
  last_login_at?: string;
  created_at: string;
}

const card: React.CSSProperties = { background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', padding: 20, fontFamily: FONT };
const btn = (bg = '#fff', color = NAVY): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 8, border: `1px solid ${bg === '#fff' ? '#D5DCE4' : bg}`,
  background: bg, color, fontFamily: FONT, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
});
const input: React.CSSProperties = { width: '100%', padding: '9px 11px', borderRadius: 8, border: '1px solid #D5DCE4', fontFamily: FONT, fontSize: 13, boxSizing: 'border-box' };
const label: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: NAVY, margin: '12px 0 5px' };

function fmt(n: number, cur = 'XAF') {
  return `${new Intl.NumberFormat('en-US').format(Math.round(n))} ${cur}`;
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,20,30,0.5)', display: 'grid', placeItems: 'center', zIndex: 300, padding: 16 }} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{ ...card, width: '100%', maxWidth: 480, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <h3 style={{ margin: 0, color: NAVY, fontSize: 16 }}>{title}</h3>
          <button style={{ ...btn(), border: 0 }} onClick={onClose} aria-label="Close">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Secrets({ title, rows, onClose }: { title: string; rows: [string, string][]; onClose: () => void }) {
  return (
    <Modal title={title} onClose={onClose}>
      <div style={{ background: '#FFF3E0', color: '#8a4b00', padding: 10, borderRadius: 8, fontSize: 12.5, margin: '8px 0' }}>
        Copy these now and send them to the merchant over a secure channel — they will not be shown again.
      </div>
      {rows.map(([k, v]) => (
        <div key={k}>
          <span style={label}>{k}</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <input readOnly value={v} style={{ ...input, fontFamily: 'monospace' }} onFocus={(e) => e.target.select()} />
            <button style={btn()} onClick={() => navigator.clipboard?.writeText(v)} aria-label={`Copy ${k}`}><Copy size={14} /></button>
          </div>
        </div>
      ))}
      <div style={{ textAlign: 'right', marginTop: 16 }}><button style={btn(NAVY, '#fff')} onClick={onClose}>I have saved them</button></div>
    </Modal>
  );
}

export default function Merchants() {
  const [list, setList] = useState<Merchant[] | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [create, setCreate] = useState(false);
  const [fund, setFund] = useState<Merchant | null>(null);
  const [secrets, setSecrets] = useState<{ title: string; rows: [string, string][] } | null>(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    const r = await api.listMerchants();
    if (r.success) { setList(asList<Merchant>(r.data)); setError(''); } else setError(r.message || 'Failed to load merchants');
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn: () => Promise<any>, after?: (r: any) => void) => {
    setBusy(true); setError('');
    const r = await fn();
    setBusy(false);
    if (!r.success) { setError(r.message || 'Something went wrong'); return; }
    after?.(r);
    load();
  };

  const submitCreate = (e: React.FormEvent) => {
    e.preventDefault();
    run(() => api.createMerchant({ name: form.name, email: form.email, phone: form.phone, ...(form.password ? { password: form.password } : {}) }), (r) => {
      const d = r.data;
      const rows: [string, string][] = [['Dashboard', d.dashboard_url || DASHBOARD_URL], ['Email', d.merchant.email]];
      rows.push(['Dashboard password', d.temporary_password || form.password]);
      rows.push(['API key (for their server)', d.api_key]);
      setCreate(false); setForm({ name: '', email: '', phone: '', password: '' });
      setSecrets({ title: 'Merchant created', rows });
    });
  };

  const submitFund = (e: React.FormEvent) => {
    e.preventDefault();
    if (!fund) return;
    run(() => api.fundMerchant(fund.id, Number(amount), note), () => { setFund(null); setAmount(''); setNote(''); });
  };

  const resetPassword = (m: Merchant) => {
    if (!window.confirm(`Generate a new dashboard password for ${m.name}? Their current password stops working.`)) return;
    run(() => api.setMerchantPassword(m.id), (r) => setSecrets({ title: `Dashboard password for ${m.name}`, rows: [['Dashboard', DASHBOARD_URL], ['Email', m.email], ['Temporary password', r.data.temporary_password]] }));
  };

  const rotate = (m: Merchant) => {
    if (!window.confirm(`Rotate the API key for ${m.name}? Their current key stops working immediately.`)) return;
    run(() => api.rotateMerchantKey(m.id), (r) => setSecrets({ title: `New API key for ${m.name}`, rows: [['API key', r.data.api_key]] }));
  };

  const toggle = (m: Merchant) => {
    if (!window.confirm(`${m.is_active ? 'Disable' : 'Enable'} ${m.name}? ${m.is_active ? 'They will be signed out and their API key stops working.' : ''}`)) return;
    run(() => api.toggleMerchant(m.id));
  };

  return (
    <div style={{ fontFamily: FONT, background: BG, minHeight: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1 style={{ margin: 0, color: NAVY, fontSize: 22, display: 'flex', alignItems: 'center', gap: 8 }}><Store size={22} /> Merchants</h1>
          <p style={{ margin: '4px 0 0', color: '#6b7c8a', fontSize: 13 }}>Businesses using the Merchants API and dashboard ({DASHBOARD_URL.replace('https://', '')}).</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={btn()} onClick={load}><RefreshCw size={14} /> Refresh</button>
          <button style={btn(GREEN, '#fff')} onClick={() => { setError(''); setCreate(true); }}><Plus size={14} /> New merchant</button>
        </div>
      </div>
      {error && <div style={{ background: '#FFEBEE', color: '#C62828', padding: 10, borderRadius: 8, marginBottom: 14, fontSize: 13 }}>{error}</div>}
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: '#6b7c8a', fontSize: 12 }}>
              {['Merchant', 'Balance', 'API key', 'Dashboard login', 'Status', 'Actions'].map((h) => <th key={h} style={{ padding: '12px 14px', borderBottom: '1px solid #EEF1F4' }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {!list && <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#6b7c8a' }}>Loading…</td></tr>}
            {list && list.length === 0 && <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#6b7c8a' }}>No merchants yet.</td></tr>}
            {list?.map((m) => (
              <tr key={m.id}>
                <td style={{ padding: '12px 14px', borderBottom: '1px solid #EEF1F4' }}><strong style={{ color: NAVY }}>{m.name}</strong><div style={{ color: '#6b7c8a', fontSize: 12 }}>{m.email}</div></td>
                <td style={{ padding: '12px 14px', borderBottom: '1px solid #EEF1F4', whiteSpace: 'nowrap' }}><strong>{fmt(m.balance, m.currency)}</strong></td>
                <td style={{ padding: '12px 14px', borderBottom: '1px solid #EEF1F4', fontFamily: 'monospace', fontSize: 12 }}>{m.api_key_prefix}…</td>
                <td style={{ padding: '12px 14px', borderBottom: '1px solid #EEF1F4', fontSize: 12 }}>
                  {m.has_password ? <span style={{ color: '#2E7D32' }}>● Set</span> : <span style={{ color: '#E65100' }}>● No password</span>}
                  <div style={{ color: '#6b7c8a' }}>{m.last_login_at ? `Last: ${new Date(m.last_login_at).toLocaleDateString()}` : 'Never signed in'}</div>
                </td>
                <td style={{ padding: '12px 14px', borderBottom: '1px solid #EEF1F4' }}>
                  <span style={{ padding: '2px 9px', borderRadius: 99, fontSize: 12, fontWeight: 600, background: m.is_active ? '#E8F5E9' : '#FFEBEE', color: m.is_active ? '#2E7D32' : '#C62828' }}>{m.is_active ? 'Active' : 'Disabled'}</span>
                </td>
                <td style={{ padding: '12px 14px', borderBottom: '1px solid #EEF1F4' }}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button style={btn()} disabled={busy} onClick={() => { setError(''); setFund(m); }}><Wallet size={13} /> Top up</button>
                    <button style={btn()} disabled={busy} onClick={() => resetPassword(m)}><Lock size={13} /> {m.has_password ? 'Reset password' : 'Set password'}</button>
                    <button style={btn()} disabled={busy} onClick={() => rotate(m)}><KeyRound size={13} /> Rotate key</button>
                    <button style={btn()} disabled={busy} onClick={() => toggle(m)}><Power size={13} /> {m.is_active ? 'Disable' : 'Enable'}</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {create && (
        <Modal title="New merchant" onClose={() => setCreate(false)}>
          <form onSubmit={submitCreate}>
            <label style={label}>Business name</label><input style={input} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <label style={label}>Email (their dashboard login)</label><input style={input} type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <label style={label}>Phone (optional)</label><input style={input} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <label style={label}>Dashboard password (leave empty to generate one)</label><input style={input} type="text" minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            <div style={{ textAlign: 'right', marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" style={btn()} onClick={() => setCreate(false)}>Cancel</button>
              <button style={btn(GREEN, '#fff')} disabled={busy}>{busy ? 'Creating…' : 'Create merchant'}</button>
            </div>
          </form>
        </Modal>
      )}

      {fund && (
        <Modal title={`Top up ${fund.name}`} onClose={() => setFund(null)}>
          <p style={{ color: '#6b7c8a', fontSize: 13, margin: '4px 0' }}>Current balance: <strong>{fmt(fund.balance, fund.currency)}</strong>. This credit appears on their statement.</p>
          <form onSubmit={submitFund}>
            <label style={label}>Amount (XAF)</label><input style={input} type="number" min="1" step="any" required value={amount} onChange={(e) => setAmount(e.target.value)} />
            <label style={label}>Note (shown on their statement)</label><input style={input} maxLength={200} placeholder="e.g. bank transfer ref 12345" value={note} onChange={(e) => setNote(e.target.value)} />
            <div style={{ textAlign: 'right', marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" style={btn()} onClick={() => setFund(null)}>Cancel</button>
              <button style={btn(GREEN, '#fff')} disabled={busy}>{busy ? 'Saving…' : 'Add funds'}</button>
            </div>
          </form>
        </Modal>
      )}

      {secrets && <Secrets title={secrets.title} rows={secrets.rows} onClose={() => setSecrets(null)} />}
    </div>
  );
}
